import React from 'react';

import { useBiopharmaGroupState, useBiopharmaGroupActions } from './BiopharmaGroupContext';
import { cacheLineImportanceForJobDescription, checkIfStillActiveForJobDescription } from './restApi';

import Table from '../../Shared/UI/Table/components/Table';
import Thead from '../../Shared/UI/Table/components/Thead';
import Tbody from '../../Shared/UI/Table/components/Tbody';
import Tr from '../../Shared/UI/Table/components/Tr';
import Th from '../../Shared/UI/Table/components/Th';
import Td from '../../Shared/UI/Table/components/Td';
import Button from '../../Shared/UI/Button/components/Button';
import Dropdown from '../../Shared/UI/Dropdown/components/Dropdown';

const getDeletionStatus = (deletionQueuedAt) => {
  if (!deletionQueuedAt) return null;

  const queuedTime = new Date(deletionQueuedAt);
  const now = new Date();
  const minutesAgo = (now - queuedTime) / (1000 * 60);

  if (minutesAgo < 5) {
    return 'pending';
  } else {
    return 'stale';
  }
};

const getRowStyle = (deletionQueuedAt) => {
  const status = getDeletionStatus(deletionQueuedAt);

  if (status === 'pending') {
    return { backgroundColor: 'var(--color-status-warning-bg)' };
  } else if (status === 'stale') {
    return { backgroundColor: 'var(--color-status-error-bg)' };
  }

  return {};
};

const BiopharmaGroupJobDescriptions = () => {
  const biopharmaGroupState = useBiopharmaGroupState();
  const {
    onResponse,
    onError,
    openDeleteJobDescriptionModal,
    openCloseJobDescriptionModal,
    openReopenJobDescriptionModal,
    openArchiveJobDescriptionModal,
    openUpdateJobDescriptionModal,
  } = useBiopharmaGroupActions();

  const authenticityToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");
  const jobDescriptions = biopharmaGroupState?.biopharmaGroup?.jobDescriptions || [];

  if (jobDescriptions.length === 0) {
    return <p>No job descriptions from active boards.</p>;
  }

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Title</Th>
          <Th>Board</Th>
          <Th>State</Th>
          <Th>Created</Th>
          <Th />
        </Tr>
      </Thead>
      <Tbody>
        {jobDescriptions.map(jd => {
          const deletionStatus = getDeletionStatus(jd.deletionQueuedAt);
          return (
            <Tr key={jd.id} style={getRowStyle(jd.deletionQueuedAt)}>
              <Td>
                {jd.sourceUrl ? (
                  <a href={jd.sourceUrl} target="_blank" rel="noopener noreferrer">{jd.title || 'Untitled'}</a>
                ) : (
                  jd.title || 'Untitled'
                )}
                {jd.closedAt && <span style={{ color: 'var(--color-status-error-fg)', marginLeft: '8px' }}>(Closed)</span>}
              </Td>
              <Td>
                {jd.externalJobBoardUrl ? (
                  <a href={jd.externalJobBoardUrl} target="_blank" rel="noopener noreferrer">{jd.externalJobBoardUrl}</a>
                ) : (
                  '—'
                )}
              </Td>
              <Td>{jd.processingState}</Td>
              <Td>{jd.createdAt}</Td>
              <Td>
                {deletionStatus ? (
                  deletionStatus === 'pending' ? 'Deleting…' : 'Delete stuck'
                ) : (
                  <Dropdown
                    trigger={<Button variant="secondary" size="sm">Actions</Button>}
                    align="right"
                  >
                    <Dropdown.Item onClick={() => openUpdateJobDescriptionModal(jd)}>
                      Update
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        cacheLineImportanceForJobDescription({
                          onResponse,
                          onError,
                          body: {
                            authenticityToken,
                            biopharmaGroupId: biopharmaGroupState?.biopharmaGroup?.id,
                            jobDescriptionId: jd.id,
                          },
                        });
                      }}
                    >
                      Cache Line Importance
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        checkIfStillActiveForJobDescription({
                          onResponse,
                          onError,
                          jobDescriptionId: jd.id,
                          biopharmaGroupId: biopharmaGroupState?.biopharmaGroup?.id,
                        });
                      }}
                    >
                      Check If Still Active
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    {!jd.closedAt && (
                      <Dropdown.Item
                        onClick={() => openCloseJobDescriptionModal(jd)}
                      >
                        Close
                      </Dropdown.Item>
                    )}
                    {jd.closedAt && (
                      <Dropdown.Item
                        onClick={() => openReopenJobDescriptionModal(jd)}
                      >
                        Reopen
                      </Dropdown.Item>
                    )}
                    {jd.closedAt && (
                      <Dropdown.Item
                        onClick={() => openArchiveJobDescriptionModal(jd)}
                        destructive
                      >
                        Archive
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item
                      onClick={() => openDeleteJobDescriptionModal(jd)}
                      destructive
                    >
                      Delete
                    </Dropdown.Item>
                  </Dropdown>
                )}
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
};

export default BiopharmaGroupJobDescriptions;
